
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { AvailabilitySelector } from "./AvailabilitySelector";
import { useState, useEffect } from "react";
import { validateNumericInput } from "./utilities/validateInput";
import { Switch } from "@/components/ui/switch";

const DAYS_OF_WEEK = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday"
];

interface Step2Props {
  formData: {
    availabilitySlots: any[];
    hasLimitedPortions: boolean;
    available_portions: string;
    deliveryAvailable: boolean;
    deliveryFee: string;
    pickup_instructions?: string;
    preparation_type: "scheduled" | "on_demand";
    prep_time_hours?: string;
    cutoff_time?: string;
    max_daily_orders?: string;
    pickup_window_start?: string;
    pickup_window_end?: string;
    available_days: string[];
    use_custom_pickup_address: boolean;
    pickup_address?: string;
    pickup_city?: string;
    pickup_state?: string;
    pickup_postal_code?: string;
  };
  onChange: (field: string, value: any) => void;
  onNext: () => void;
  onBack: () => void;
}

export const Step2DeliveryInfo = ({
  formData,
  onChange,
  onNext,
  onBack,
}: Step2Props) => {
  const [showOnDemandSettings, setShowOnDemandSettings] = useState(
    formData.preparation_type === "on_demand"
  );
  const [errors, setErrors] = useState<{[key: string]: string | null}>({
    prep_time_hours: null,
    availabilitySlots: null,
    pickup_instructions: null,
    cutoff_time: null,
    pickup_window_start: null,
    pickup_window_end: null,
    available_days: null,
    pickup_address: null,
    pickup_city: null,
    pickup_state: null,
    pickup_postal_code: null
  });

  const handlePrepTimeChange = (value: string) => {
    const error = validateNumericInput(value, 'nutritional');
    setErrors(prev => ({ ...prev, prep_time_hours: error }));
    if (!error) {
      onChange("prep_time_hours", value);
    }
  };

  const validateForm = () => {
    const newErrors = { ...errors };
    let isValid = true;

    // Clear previous errors
    Object.keys(newErrors).forEach(key => {
      newErrors[key] = null;
    });

    // Required for all preparation types
    if (!formData.pickup_instructions) {
      newErrors.pickup_instructions = "Pickup instructions are required";
      isValid = false;
    }

    // Custom address validation if toggle is on
    if (formData.use_custom_pickup_address) {
      if (!formData.pickup_address) {
        newErrors.pickup_address = "Street address is required when using custom pickup address";
        isValid = false;
      }
      if (!formData.pickup_city) {
        newErrors.pickup_city = "City is required when using custom pickup address";
        isValid = false;
      }
      if (!formData.pickup_state) {
        newErrors.pickup_state = "State is required when using custom pickup address";
        isValid = false;
      }
      if (!formData.pickup_postal_code) {
        newErrors.pickup_postal_code = "ZIP code is required when using custom pickup address";
        isValid = false;
      }
    }

    if (formData.preparation_type === "scheduled") {
      // For scheduled meals, at least one availability slot is required
      if (!formData.availabilitySlots || formData.availabilitySlots.length === 0) {
        newErrors.availabilitySlots = "At least one availability slot is required";
        isValid = false;
      }
    } else if (formData.preparation_type === "on_demand") {
      // For on-demand meals
      if (!formData.prep_time_hours) {
        newErrors.prep_time_hours = "Preparation time is required";
        isValid = false;
      }
      
      if (!formData.cutoff_time) {
        newErrors.cutoff_time = "Daily order cutoff time is required";
        isValid = false;
      }
      
      if (!formData.pickup_window_start) {
        newErrors.pickup_window_start = "Pickup window start time is required";
        isValid = false;
      }
      
      if (!formData.pickup_window_end) {
        newErrors.pickup_window_end = "Pickup window end time is required";
        isValid = false;
      }
      
      if (!formData.available_days || formData.available_days.length === 0) {
        newErrors.available_days = "At least one available day must be selected";
        isValid = false;
      }
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleNext = () => {
    if (validateForm()) {
      onNext();
    }
  };

  return (
    <div className="space-y-8 max-w-2xl mx-auto">
      <div className="text-center">
        <h1 className="text-2xl md:text-[28px] font-medium mb-3 text-black">Set Availability</h1>
        <p className="text-[#6B7280] text-base">
          Choose how you want to prepare and serve this meal.
        </p>
      </div>

      <div className="space-y-4">
        <Label className="text-black text-base">Preparation Type</Label>
        <RadioGroup
          value={formData.preparation_type}
          onValueChange={(value) => {
            onChange("preparation_type", value);
            setShowOnDemandSettings(value === "on_demand");
          }}
          className="flex flex-col sm:grid sm:grid-cols-2 gap-4"
        >
          <div className="flex flex-col space-y-2 border rounded-xl p-6 hover:border-black transition-colors cursor-pointer">
            <div className="flex items-start space-x-3">
              <RadioGroupItem value="scheduled" id="scheduled" />
              <div className="flex-1">
                <Label htmlFor="scheduled" className="cursor-pointer">
                  <div className="font-medium text-lg mb-1">Scheduled</div>
                  <p className="text-sm text-muted-foreground">
                    Set specific dates and times when the meal will be available
                  </p>
                </Label>
              </div>
            </div>
          </div>
          <div className="flex flex-col space-y-2 border rounded-xl p-6 hover:border-black transition-colors cursor-pointer">
            <div className="flex items-start space-x-3">
              <RadioGroupItem value="on_demand" id="on_demand" />
              <div className="flex-1">
                <Label htmlFor="on_demand" className="cursor-pointer">
                  <div className="font-medium text-lg mb-1">On Demand</div>
                  <p className="text-sm text-muted-foreground">
                    Prepare meals after orders are placed with advance notice
                  </p>
                </Label>
              </div>
            </div>
          </div>
        </RadioGroup>
      </div>

      {!showOnDemandSettings ? (
        <div className="space-y-2.5">
          <Label className="text-black text-base">Availability Slots *</Label>
          <AvailabilitySelector
            selectedSlots={formData.availabilitySlots}
            onSlotsChange={(slots) => onChange("availabilitySlots", slots)}
            hasLimitedPortions={formData.hasLimitedPortions}
            availablePortions={formData.available_portions}
            onHasLimitedPortionsChange={(value) => onChange("hasLimitedPortions", value)}
            onAvailablePortionsChange={(value) => onChange("available_portions", value)}
          />
          {errors.availabilitySlots && (
            <p className="text-sm text-red-500 mt-1">{errors.availabilitySlots}</p>
          )}
        </div>
      ) : (
        <div className="space-y-8">
          <div className="grid gap-6 sm:grid-cols-2">
            <div className="space-y-4">
              <Label className="text-black">Preparation Time (hours) *</Label>
              <Input
                type="text"
                value={formData.prep_time_hours || ""}
                onChange={(e) => handlePrepTimeChange(e.target.value)}
                placeholder="e.g., 48"
                className={`h-12 rounded-xl border-[#E5E5E5] bg-[#EDEDED] ${
                  errors.prep_time_hours ? 'border-red-500 focus-visible:ring-red-500' : ''
                }`}
              />
              {errors.prep_time_hours && (
                <p className="text-sm text-red-500 mt-1">{errors.prep_time_hours}</p>
              )}
              <p className="text-sm text-muted-foreground">
                Hours needed to prepare after receiving an order
              </p>
            </div>

            <div className="space-y-4">
              <Label className="text-black">Maximum Daily Orders</Label>
              <Input
                type="number"
                value={formData.max_daily_orders || ""}
                onChange={(e) => onChange("max_daily_orders", e.target.value)}
                placeholder="e.g., 5"
                min="1"
                className="h-12 rounded-xl border-[#E5E5E5] bg-[#EDEDED]"
              />
            </div>
          </div>

          <div className="grid gap-6 sm:grid-cols-2">
            <div className="space-y-4">
              <Label className="text-black">Daily Order Cutoff Time *</Label>
              <Input
                type="time"
                value={formData.cutoff_time || ""}
                onChange={(e) => onChange("cutoff_time", e.target.value)}
                className={`h-12 rounded-xl border-[#E5E5E5] bg-[#EDEDED] ${
                  errors.cutoff_time ? 'border-red-500 focus-visible:ring-red-500' : ''
                }`}
              />
              {errors.cutoff_time && (
                <p className="text-sm text-red-500 mt-1">{errors.cutoff_time}</p>
              )}
              <p className="text-sm text-muted-foreground">
                Last time to place orders for next day
              </p>
            </div>

            <div className="space-y-4">
              <Label className="text-black">Pickup Window *</Label>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Input
                    type="time"
                    value={formData.pickup_window_start || ""}
                    onChange={(e) => onChange("pickup_window_start", e.target.value)}
                    className={`h-12 rounded-xl border-[#E5E5E5] bg-[#EDEDED] ${
                      errors.pickup_window_start ? 'border-red-500 focus-visible:ring-red-500' : ''
                    }`}
                  />
                  {errors.pickup_window_start && (
                    <p className="text-sm text-red-500 mt-1">{errors.pickup_window_start}</p>
                  )}
                </div>
                <div>
                  <Input
                    type="time"
                    value={formData.pickup_window_end || ""}
                    onChange={(e) => onChange("pickup_window_end", e.target.value)}
                    className={`h-12 rounded-xl border-[#E5E5E5] bg-[#EDEDED] ${
                      errors.pickup_window_end ? 'border-red-500 focus-visible:ring-red-500' : ''
                    }`}
                  />
                  {errors.pickup_window_end && (
                    <p className="text-sm text-red-500 mt-1">{errors.pickup_window_end}</p>
                  )}
                </div>
              </div>
              <p className="text-sm text-muted-foreground">
                Time window when customers can pick up their order
              </p>
            </div>
          </div>

          <div className="space-y-4">
            <Label className="text-black">Available Days *</Label>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {DAYS_OF_WEEK.map((day) => (
                <div key={day} className="flex items-center space-x-2">
                  <Checkbox
                    id={day}
                    checked={formData.available_days?.includes(day)}
                    onCheckedChange={(checked) => {
                      const currentDays = formData.available_days || [];
                      onChange(
                        "available_days",
                        checked
                          ? [...currentDays, day]
                          : currentDays.filter((d) => d !== day)
                      );
                    }}
                  />
                  <Label htmlFor={day}>{day}</Label>
                </div>
              ))}
            </div>
            {errors.available_days && (
              <p className="text-sm text-red-500 mt-1">{errors.available_days}</p>
            )}
          </div>
        </div>
      )}

      {/* Custom Pickup Address Section */}
      <div className="space-y-4 border-t pt-6">
        <div className="flex items-center justify-between">
          <Label htmlFor="customAddressToggle" className="text-base font-medium text-black">
            Use a custom pickup address for this meal
          </Label>
          <Switch
            id="customAddressToggle"
            checked={formData.use_custom_pickup_address || false}
            onCheckedChange={(checked) => onChange("use_custom_pickup_address", checked)}
          />
        </div>
        
        {formData.use_custom_pickup_address && (
          <div className="space-y-4 mt-4 p-4 bg-gray-50 rounded-xl border border-gray-200">
            <div className="space-y-2.5">
              <Label htmlFor="pickup_address" className="text-black">Street Address *</Label>
              <Input
                id="pickup_address"
                value={formData.pickup_address || ""}
                onChange={(e) => onChange("pickup_address", e.target.value)}
                placeholder="Enter street address"
                className={`h-12 rounded-xl border-[#E5E5E5] bg-[#EDEDED] ${
                  errors.pickup_address ? 'border-red-500 focus-visible:ring-red-500' : ''
                }`}
              />
              {errors.pickup_address && (
                <p className="text-sm text-red-500 mt-1">{errors.pickup_address}</p>
              )}
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="space-y-2.5">
                <Label htmlFor="pickup_city" className="text-black">City *</Label>
                <Input
                  id="pickup_city"
                  value={formData.pickup_city || ""}
                  onChange={(e) => onChange("pickup_city", e.target.value)}
                  placeholder="City"
                  className={`h-12 rounded-xl border-[#E5E5E5] bg-[#EDEDED] ${
                    errors.pickup_city ? 'border-red-500 focus-visible:ring-red-500' : ''
                  }`}
                />
                {errors.pickup_city && (
                  <p className="text-sm text-red-500 mt-1">{errors.pickup_city}</p>
                )}
              </div>
              
              <div className="space-y-2.5">
                <Label htmlFor="pickup_state" className="text-black">State *</Label>
                <Input
                  id="pickup_state"
                  value={formData.pickup_state || ""}
                  onChange={(e) => onChange("pickup_state", e.target.value)}
                  placeholder="State"
                  className={`h-12 rounded-xl border-[#E5E5E5] bg-[#EDEDED] ${
                    errors.pickup_state ? 'border-red-500 focus-visible:ring-red-500' : ''
                  }`}
                />
                {errors.pickup_state && (
                  <p className="text-sm text-red-500 mt-1">{errors.pickup_state}</p>
                )}
              </div>
              
              <div className="space-y-2.5">
                <Label htmlFor="pickup_postal_code" className="text-black">ZIP Code *</Label>
                <Input
                  id="pickup_postal_code"
                  value={formData.pickup_postal_code || ""}
                  onChange={(e) => onChange("pickup_postal_code", e.target.value)}
                  placeholder="ZIP Code"
                  className={`h-12 rounded-xl border-[#E5E5E5] bg-[#EDEDED] ${
                    errors.pickup_postal_code ? 'border-red-500 focus-visible:ring-red-500' : ''
                  }`}
                />
                {errors.pickup_postal_code && (
                  <p className="text-sm text-red-500 mt-1">{errors.pickup_postal_code}</p>
                )}
              </div>
            </div>
            
            <p className="text-sm text-muted-foreground">
              This address will be used instead of your profile address for customer pickups.
            </p>
          </div>
        )}
        
        {!formData.use_custom_pickup_address && (
          <p className="text-sm text-muted-foreground mt-2">
            Your profile address will be used for customer pickups.
          </p>
        )}
      </div>

      <div className="space-y-4 border-t pt-8">
        <div className="flex items-center space-x-2">
          <Checkbox
            id="deliveryAvailable"
            checked={formData.deliveryAvailable}
            onCheckedChange={(checked) => onChange("deliveryAvailable", checked)}
          />
          <Label htmlFor="deliveryAvailable" className="text-sm font-medium text-black">
            Delivery available
          </Label>
        </div>

        {formData.deliveryAvailable && (
          <div className="space-y-2 ml-6">
            <Label htmlFor="deliveryFee" className="text-black">
              Delivery Fee ($)
            </Label>
            <Input
              id="deliveryFee"
              type="number"
              value={formData.deliveryFee}
              onChange={(e) => onChange("deliveryFee", e.target.value)}
              placeholder="Enter delivery fee"
              min="0"
              step="0.01"
              className="h-12 rounded-xl border-[#E5E5E5] bg-[#EDEDED] max-w-[200px]"
            />
          </div>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="pickupInstructions" className="text-black">
          Pickup Instructions *
        </Label>
        <Textarea
          id="pickupInstructions"
          value={formData.pickup_instructions}
          onChange={(e) => onChange("pickup_instructions", e.target.value)}
          placeholder="Enter instructions for customers picking up their order (e.g., 'Ring doorbell, pickup at side entrance')"
          className={`min-h-[100px] rounded-xl border-[#E5E5E5] bg-[#EDEDED] ${
            errors.pickup_instructions ? 'border-red-500 focus-visible:ring-red-500' : ''
          }`}
        />
        {errors.pickup_instructions && (
          <p className="text-sm text-red-500 mt-1">{errors.pickup_instructions}</p>
        )}
      </div>

      <div className="flex justify-between pt-4">
        <Button
          variant="outline"
          onClick={onBack}
          className="px-8 py-3 h-12 rounded-full text-base font-medium border-[#E5E5E5] hover:bg-[#F9FAFB] text-black"
        >
          Back
        </Button>
        <Button
          onClick={handleNext}
          className="bg-black hover:bg-black/90 text-white px-8 py-3 h-12 rounded-full text-base font-medium transition-colors"
        >
          Next
        </Button>
      </div>
    </div>
  );
};
